<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { OcrCompletionSnapshot } from '@/domain/types'

const props = defineProps<{ completion?: OcrCompletionSnapshot; retrying?: boolean; preprocessRejectedLabel?: string }>()
const { t, locale } = useI18n()
const message = computed(() => {
  const c = props.completion
  if (!c || !['low-confidence', 'parse-failed', 'no-text'].includes(c.status)) return ''
  if (c.failureReason === 'preprocess-rejected') return props.preprocessRejectedLabel ?? t('ocrQuality.preprocessRejected')
  if (c.status === 'low-confidence' && c.rawText.trim()
    && c.confidence !== undefined && Number.isFinite(c.confidence)
    && c.minimumConfidence !== undefined && Number.isFinite(c.minimumConfidence)
    && c.confidence < c.minimumConfidence) {
    const percent = (value: number) => new Intl.NumberFormat(locale.value,
      { style: 'percent', maximumFractionDigits: 2 }).format(value)
    return t('ocrQuality.lowConfidence', { text: c.rawText, confidence: percent(c.confidence),
      minimumConfidence: percent(c.minimumConfidence) })
  }
  return t('ocrQuality.failed', { reason: t(`calibration.failureReasons.${c.status}`) })
})
</script>

<template>
  <p v-if="message" class="level-ocr-failure" role="status" aria-live="polite" aria-atomic="true">
    {{ message }}<span v-if="retrying"> {{ t('ocrQuality.retrying') }}</span>
  </p>
</template>

<style scoped>
.level-ocr-failure { margin: .5rem 0 0; color: var(--text-strong); font-size: var(--font-caption); line-height: 1.6; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
</style>
